import { interval, of } from 'rxjs'
import {
  catchError,
  delayWhen,
  distinctUntilChanged,
  filter,
  map,
  merge,
  startWith,
  switchMap,
  takeUntil
} from 'rxjs/operators'
import { ofType } from 'redux-observable'
import {
  fetchPackageStock,
  fetchPickersBindingsStock,
  fetchProductStock,
  fetchSaleStock
} from 'api/planet/stock'
import { LOCATION_CHANGE } from 'connected-react-router'
import {
  getLocationChangeQuery,
  isCatalogOrUniverseOrSaleSlugPageAction,
  isKeyUnchanged,
  isProductPageAction,
  isSaleHomePageAction
} from 'domain/router'
import { isArray, isObject } from 'helpers/typeof'
import { captureException } from 'helpers/raven'
import * as Actions from 'store/actions/index.ts'
import {
  getActivePackageKey,
  getCountryCode,
  getCultureCode,
  getCurrentProductKey,
  getHasPackages,
  getLoggedIn,
  getMarketId,
  getSaleKey
} from '../selectors'

// type IsStockFetchResponseValid = any => boolean
const isStockFetchResponseValid = stocks => (
  isArray(stocks)
)

// type IsPickersBindingsStockFetchValid = any => boolean
const isPickersBindingsStockFetchValid = stocks => (
  isObject(stocks) &&
  Boolean(stocks) && Boolean(stocks.pickers)
)

// type OnInvalidSaleStockFetchResponse = (Error, Object, boolean) => any // (Error, Object, boolean) => FetchSaleStockErrorAction
const onInvalidSaleStockFetchResponse = (error, dataToTrack, isSentryEnabled) => {
  if (isSentryEnabled) {
    captureException(new Error('Unable to fetch sale stock'), {
      extra: { error, ...dataToTrack }
    })
  }

  return Actions.stock.onSaleStockFetchError(error)
}

export const saleStocksEpic = (actions$, state$) => actions$.pipe(
  ofType(Actions.member.MEMBER_INFO_FETCHED),
  switchMap(
    () => {
      const saleKey = getSaleKey(state$.value)
      const marketId = getMarketId(state$.value)
      const cultureCode = getCultureCode(state$.value)

      const fetchAndHandleSaleStock = () => fetchSaleStock(saleKey, marketId, cultureCode).pipe(
        map(({ response }) => (
          isStockFetchResponseValid(response)
            ? Actions.stock.onSaleStockFetched(response, saleKey)
            : onInvalidSaleStockFetchResponse(new Error('Corrupted sale stock'), { saleKey }, false)
        )),
        catchError(error => of(onInvalidSaleStockFetchResponse(error, { saleKey }, true))),
        startWith(Actions.fetch.onFetchStarted())
      )

      return interval(5 * 60 * 1000).pipe(
        switchMap(fetchAndHandleSaleStock),
        merge(fetchAndHandleSaleStock())
      )
    }
  )
)

// type OnInvalidProductStockFetchResponse = (Error, Object, boolean) => any // (Error, Object, boolean) => FetchProductStockErrorAction
const onInvalidProductStockFetchResponse = (error, dataToTrack, isSentryEnabled) => {
  if (isSentryEnabled) {
    captureException(new Error('Unable to fetch product stock'), {
      extra: { error, ...dataToTrack }
    })
  }

  return Actions.stock.onProductStockFetchError(error)
}

const isValidResource = (resources, key) => resources[key] && resources[key].key

export const productStocksEpic = (actions$, state$) => {
  const authentications$ = actions$.pipe(ofType(Actions.member.MEMBER_INFO_FETCHED))

  return actions$.pipe(
    ofType(LOCATION_CHANGE),
    distinctUntilChanged(isKeyUnchanged),
    filter(isProductPageAction),
    map(getLocationChangeQuery),
    delayWhen(() => {
      if (getLoggedIn(state$.value)) { return of({}) }

      return authentications$
    }),
    switchMap(
      ({ key }) => {
        const saleKey = getSaleKey(state$.value)
        const marketId = getMarketId(state$.value)
        const cultureCode = getCultureCode(state$.value)

        const fetchAndHandleProductStock = () =>
          fetchProductStock(saleKey, key, marketId, cultureCode).pipe(
            map(({ response }) => (
              isStockFetchResponseValid(response)
                ? Actions.stock.onProductStockFetched(response, key)
                : onInvalidProductStockFetchResponse(new Error('Corrupted product stock'), { key, saleKey }, false)
            )),
            catchError(error => of(onInvalidSaleStockFetchResponse(error, { key, saleKey }, true))),
            startWith(Actions.fetch.onFetchStarted())
          )

        const handleVersionChange$ = actions$.pipe(
          ofType(Actions.version.VERSION_CHANGED)
        )

        return interval(1.5 * 60 * 1000).pipe(
          merge(handleVersionChange$),
          takeUntil(actions$.pipe(
            ofType(LOCATION_CHANGE),
            filter(act => isSaleHomePageAction(act) || isCatalogOrUniverseOrSaleSlugPageAction(act) || (isValidResource(state$.value.resources, key) && !getHasPackages(state$.value)))
          )),
          switchMap(fetchAndHandleProductStock),
          merge(fetchAndHandleProductStock())
        )
      }
    )
  )
}

const onInvalidPackageStockFetchResponse = (error, dataToTrack, isSentryEnabled) => {
  if (isSentryEnabled) {
    captureException(new Error('Unable to fetch package stock'), {
      extra: { error, ...dataToTrack }
    })
  }

  return Actions.stock.onPackageStockFetchError()
}

export const packageStocksEpic = (actions$, state$) => actions$.pipe(
  ofType(Actions.configurator.PACKAGE_ELEMENTS_FETCHED),
  switchMap(
    () => {
      const state = state$.value
      const saleKey = getSaleKey(state)
      const productKey = getCurrentProductKey(state)
      const packageKey = getActivePackageKey(state)
      const marketId = getMarketId(state)
      const cultureCode = getCultureCode(state)

      const fetchAndHandlePackageStock = () => fetchPackageStock(saleKey, productKey, packageKey, marketId, cultureCode).pipe(
        map(({ response }) => (
          isStockFetchResponseValid(response)
            ? Actions.stock.onPackageStockFetched(response, packageKey)
            : onInvalidPackageStockFetchResponse('Corrupted package stock', { packageKey, productKey, saleKey }, false)
        )),
        catchError(error => of(onInvalidPackageStockFetchResponse(error, { packageKey, productKey, saleKey }, true))),
        startWith(Actions.fetch.onFetchStarted())
      )

      const handleVersionChange$ = actions$.pipe(
        ofType(Actions.version.VERSION_CHANGED)
      )

      return interval(1.5 * 60 * 1000).pipe(
        merge(handleVersionChange$),
        takeUntil(actions$.pipe(
          ofType(LOCATION_CHANGE, Actions.package.SELECT_PACKAGE),
          filter(action => action.type === Actions.package.SELECT_PACKAGE || (action.type === LOCATION_CHANGE &&
            (isCatalogOrUniverseOrSaleSlugPageAction(action) || isSaleHomePageAction(action) ||
            (isValidResource(state$.value.resources, productKey) && !getHasPackages(state$.value))))
          )
        )),
        switchMap(fetchAndHandlePackageStock),
        merge(fetchAndHandlePackageStock())
      )
    }
  )
)

const onInvalidPickersBindingsStockFetchResponse = (error, dataToTrack, isSentryEnabled) => {
  if (isSentryEnabled) {
    captureException(new Error('Unable to fetch pickers binding stock'), {
      extra: { error, ...dataToTrack }
    })
  }

  return Actions.stock.onPickersBindingsStockFetchError()
}

export const pickersBindingsStockEpic = (actions$, state$) => actions$.pipe(
  ofType(Actions.package.SELECT_PACKAGE),
  switchMap(
    () => {
      const state = state$.value
      const saleKey = getSaleKey(state)
      const productKey = getCurrentProductKey(state)
      const packageKey = getActivePackageKey(state)
      const marketId = getMarketId(state)
      const cultureCode = getCultureCode(state)
      const countryCode = getCountryCode(state)

      const fetchAndHandlePickersBindingsStock = () => fetchPickersBindingsStock(cultureCode, countryCode, marketId, packageKey, productKey, saleKey).pipe(
        map(({ response }) => (
          isPickersBindingsStockFetchValid(response)
            ? Actions.stock.onPickersBindingsStockFetched(packageKey, response)
            : onInvalidPickersBindingsStockFetchResponse('Corrupted pickers bindings stock', { packageKey, productKey, saleKey }, false)
        )),
        catchError(error => of(onInvalidPickersBindingsStockFetchResponse(error, { packageKey, productKey, saleKey }, true))),
        startWith(Actions.fetch.onFetchStarted())
      )

      const handleVersionChange$ = actions$.pipe(
        ofType(Actions.version.VERSION_CHANGED)
      )

      return interval(1.5 * 60 * 1000).pipe(
        merge(handleVersionChange$),
        takeUntil(actions$.pipe(
          ofType(LOCATION_CHANGE, Actions.package.SELECT_PACKAGE),
          filter(action => action.type === Actions.package.SELECT_PACKAGE || (action.type === LOCATION_CHANGE &&
            (isCatalogOrUniverseOrSaleSlugPageAction(action) || isSaleHomePageAction(action) ||
            (isValidResource(state$.value.resources, productKey) && !getHasPackages(state$.value))))
          )
        )),
        switchMap(fetchAndHandlePickersBindingsStock),
        merge(fetchAndHandlePickersBindingsStock())
      )
    }
  )
)
